import { useEffect, type RefObject } from "react";

import { uploadImage } from "@/api/uploads";
import type { CursorInputContextValue } from "@/components/layout/cursor-input-context";
import type { ImageAttachment } from "@/components/panels/image-preview-strip";
import type { ScreenshotScreenMeta } from "@/lib/pointing-transform";
import { registerOutboundScreensForImages } from "./outbound-screenshot-metadata";
import {
  captureScreenshotDisplayAsFile,
  type ScreenshotPreview,
} from "./use-screenshot-capture";
import {
  setTextAttentionCapture,
  textChapterId,
  useTextAttentionStore,
} from "./text-attention-store";

function chapterStillNeeded(chapterId: string): boolean {
  return useTextAttentionStore
    .getState()
    .annotations.some(
      (item) =>
        textChapterId(item.id.split("/")[0]!, item.displayId, item.revision) ===
        chapterId,
    );
}

export type TextAttentionRetryPreview = {
  file: File;
  screen: ScreenshotScreenMeta;
  preview: ScreenshotPreview;
};

async function attachCapture(
  chapterId: string,
  displayId: number,
  revision: number,
  file: File,
  screen: ScreenshotScreenMeta,
  ctxRef: RefObject<CursorInputContextValue>,
): Promise<void> {
  try {
    const uploaded = await uploadImage(file);
    if (!chapterStillNeeded(chapterId)) return;
    const attachment: ImageAttachment = {
      id: `text-attention-${chapterId}`,
      url: uploaded.url,
      imageId: uploaded.id,
      width: uploaded.width,
      height: uploaded.height,
      filename: uploaded.original_filename,
    };
    const attach = ctxRef.current?.onAttachImages;
    if (!attach) {
      setTextAttentionCapture({
        chapterId,
        displayId,
        revision,
        status: "error",
      });
      return;
    }
    registerOutboundScreensForImages([uploaded.id], [screen]);
    setTextAttentionCapture({
      chapterId,
      displayId,
      revision,
      status: "ready",
      attachment,
      screen,
    });
    attach([attachment]);
  } catch {
    if (chapterStillNeeded(chapterId)) {
      setTextAttentionCapture({
        chapterId,
        displayId,
        revision,
        status: "error",
      });
    }
  }
}

async function captureChapter(
  chapterId: string,
  displayId: number,
  revision: number,
  ctxRef: RefObject<CursorInputContextValue>,
): Promise<void> {
  try {
    const result = await captureScreenshotDisplayAsFile(displayId);
    if (!chapterStillNeeded(chapterId)) return;
    const state = useTextAttentionStore.getState();
    if (
      (state.revisions[String(displayId)] ?? 0) !== revision ||
      state.changing[String(displayId)] ||
      !result.file ||
      !result.screen
    ) {
      setTextAttentionCapture({
        chapterId,
        displayId,
        revision,
        status: "error",
      });
      return;
    }
    await attachCapture(
      chapterId,
      displayId,
      revision,
      result.file,
      result.screen,
      ctxRef,
    );
  } catch {
    if (chapterStillNeeded(chapterId)) {
      setTextAttentionCapture({
        chapterId,
        displayId,
        revision,
        status: "error",
      });
    }
  }
}

export async function previewTextAttentionRetry(
  chapterId: string,
): Promise<TextAttentionRetryPreview | null> {
  const capture = useTextAttentionStore.getState().captures[chapterId];
  if (!capture || capture.status !== "error") return null;
  const result = await captureScreenshotDisplayAsFile(capture.displayId);
  if (
    !result.file ||
    !result.screen ||
    !result.preview ||
    !chapterStillNeeded(chapterId)
  )
    return null;
  return { file: result.file, screen: result.screen, preview: result.preview };
}

export async function acceptTextAttentionRetry(
  chapterId: string,
  candidate: TextAttentionRetryPreview,
  ctxRef: RefObject<CursorInputContextValue>,
): Promise<void> {
  const capture = useTextAttentionStore.getState().captures[chapterId];
  if (
    !capture ||
    capture.status !== "error" ||
    !chapterStillNeeded(chapterId) ||
    candidate.screen.displayId !== capture.displayId
  )
    return;
  setTextAttentionCapture({ ...capture, status: "capturing" });
  await attachCapture(
    chapterId,
    capture.displayId,
    capture.revision,
    candidate.file,
    candidate.screen,
    ctxRef,
  );
}

/** One capture per display/view revision, even when it has several annotations. */
export function useTextAttentionCapture(
  enabled: boolean,
  ctxRef: RefObject<CursorInputContextValue>,
): void {
  useEffect(() => {
    if (!enabled) return;
    const scan = () => {
      const state = useTextAttentionStore.getState();
      for (const annotation of state.annotations) {
        const recordingId = annotation.id.split("/")[0]!;
        const chapterId = textChapterId(
          recordingId,
          annotation.displayId,
          annotation.revision,
        );
        if (useTextAttentionStore.getState().captures[chapterId]) continue;
        setTextAttentionCapture({
          chapterId,
          displayId: annotation.displayId,
          revision: annotation.revision,
          status: "capturing",
        });
        void captureChapter(
          chapterId,
          annotation.displayId,
          annotation.revision,
          ctxRef,
        );
      }
    };
    const unsubscribe = useTextAttentionStore.subscribe(scan);
    scan();
    return unsubscribe;
  }, [enabled, ctxRef]);
}
